import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Breadcrumb from '@/components/feature/Breadcrumb';
import EventCard from '@/components/feature/EventCard';
import { events } from '@/mocks/events';
import { news } from '@/mocks/news';
import { analyses } from '@/mocks/analyses';
import { usePageMeta } from '@/hooks/usePageMeta';

const hotWords = ['黑5数据', '英超', 'NBA', 'LPL', '温网', '欧冠', '数据分析'];

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const queryParam = params.get('q') ?? '';
  const [keyword, setKeyword] = useState(queryParam);
  const [activeTab, setActiveTab] = useState<'all' | 'news' | 'analysis'>('all');

  useEffect(() => {
    setKeyword(queryParam);
  }, [queryParam]);

  const q = queryParam.trim().toLowerCase();

  const matchedEvents = useMemo(() => {
    if (!q) return [];
    return events.filter((item) =>
      [item.title, item.homeTeam, item.awayTeam, item.league, item.summary, item.tags.join(' '), String(item.black5Index)]
        .join(' ')
        .toLowerCase()
        .includes(q)
    );
  }, [q]);

  const matchedNews = useMemo(() => {
    if (!q) return [];
    return news.filter((item) =>
      [item.title, item.summary, item.tag].join(' ').toLowerCase().includes(q)
    );
  }, [q]);

  const matchedAnalyses = useMemo(() => {
    if (!q) return [];
    return analyses.filter((item) =>
      [item.title, item.subtitle, item.intro].join(' ').toLowerCase().includes(q)
    );
  }, [q]);

  const newsTotal = matchedEvents.length + matchedNews.length;
  const total = newsTotal + matchedAnalyses.length;

  usePageMeta({
    title: queryParam ? `“${queryParam}”搜索结果 - 51每日大赛` : '搜索结果 - 51每日大赛',
    description: queryParam
      ? `51每日大赛搜索“${queryParam}”的相关结果，共 ${total} 条，包含赛事资讯与数据分析专题内容。`
      : '51每日大赛搜索页，支持赛事名称、球队、黑5数据关键词的模糊查询，快速找到相关赛事资讯与数据分析。',
    keywords: '搜索,赛事资讯,黑5数据,51每日大赛',
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    if (value) {
      setParams({ q: value });
    } else {
      setParams({});
    }
  };

  const showNews = activeTab === 'all' || activeTab === 'news';
  const showAnalysis = activeTab === 'all' || activeTab === 'analysis';

  return (
    <div className="w-full px-4 md:px-6 pt-6 md:pt-8 pb-10 md:pb-12">
      <Breadcrumb items={[{ label: '首页', to: '/' }, { label: '搜索结果' }]} />

      <div className="mt-4 bg-background-50 border border-background-200 rounded-lg p-5 md:p-7">
        <h1 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">
          {queryParam ? `“${queryParam}”的搜索结果` : '全站搜索'}
        </h1>
        <form onSubmit={submit} className="mt-4 max-w-2xl">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 w-4 h-4 flex items-center justify-center" />
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                placeholder="输入赛事名称、球队或黑5数据关键词"
                className="w-full bg-background-100 border border-background-200 rounded-md pl-9 pr-3 py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="shrink-0 px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap"
            >
              重新搜索
            </button>
          </div>
        </form>

        <div className="mt-4 flex items-center gap-2 flex-wrap">
          <span className="text-xs text-foreground-500">热门关键词：</span>
          {hotWords.map((word) => (
            <button
              key={word}
              type="button"
              onClick={() => setParams({ q: word })}
              className="px-3 py-1.5 rounded-full text-xs bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors whitespace-nowrap"
            >
              {word}
            </button>
          ))}
        </div>
      </div>

      {queryParam ? (
        <>
          <div className="mt-6 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-foreground-700">
              共找到 <strong className="font-heading text-accent-600">{total}</strong> 条与
              <strong className="text-foreground-950"> “{queryParam}” </strong>相关的内容
            </p>
            <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
              {[
                { id: 'all', label: `全部 ${total}` },
                { id: 'news', label: `赛事资讯 ${newsTotal}` },
                { id: 'analysis', label: `数据分析 ${matchedAnalyses.length}` },
              ].map((tab) => (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id as typeof activeTab)}
                  className={`px-4 py-1.5 rounded-full text-xs whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? 'bg-background-50 text-primary-700 font-medium'
                      : 'text-foreground-600 hover:text-foreground-900'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>

          {total === 0 ? (
            <div className="mt-8 bg-background-50 border border-dashed border-background-300 rounded-lg py-16 px-6 text-center">
              <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center mx-auto">
                <i className="ri-search-eye-line w-7 h-7 flex items-center justify-center text-3xl" />
              </span>
              <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
                未找到相关内容
              </h2>
              <p className="mt-2 text-sm text-foreground-600">
                没有匹配 “{queryParam}” 的赛事或数据专题，建议换个关键词，例如“英超”“黑5数据”或球队名称。
              </p>
              <div className="mt-5 flex items-center justify-center gap-2 flex-wrap">
                {hotWords.slice(0, 4).map((word) => (
                  <button
                    key={word}
                    type="button"
                    onClick={() => setParams({ q: word })}
                    className="px-3 py-1.5 rounded-full text-xs bg-background-100 border border-background-200 text-foreground-700 hover:border-primary-300 transition-colors whitespace-nowrap"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-5 flex flex-col gap-8">
              {showNews && newsTotal > 0 ? (
                <section>
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2 mb-4">
                    <i className="ri-newspaper-line text-primary-600 w-5 h-5 flex items-center justify-center" />
                    赛事资讯
                    <span className="text-xs font-normal text-foreground-500">({newsTotal})</span>
                  </h2>
                  <div className="flex flex-col gap-3">
                    {matchedEvents.map((item) => (
                      <EventCard key={item.id} event={item} />
                    ))}
                  </div>
                  {matchedNews.length > 0 ? (
                    <ul className="mt-4 bg-background-50 border border-background-200 rounded-lg divide-y divide-background-200 overflow-hidden">
                      {matchedNews.map((item) => (
                        <li key={item.id}>
                          <Link
                            to={`/detail/${item.eventId}`}
                            className="group flex items-start gap-3 px-4 md:px-5 py-4 hover:bg-background-100/70 transition-colors"
                          >
                            <span className="mt-0.5 shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-secondary-100 text-secondary-800">
                              {item.tag}
                            </span>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-1">
                                {item.title}
                              </h3>
                              <p className="mt-1 text-xs text-foreground-600 line-clamp-2">{item.summary}</p>
                              <span className="mt-1 inline-block text-[11px] text-foreground-500">{item.time}</span>
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </section>
              ) : null}

              {showAnalysis && matchedAnalyses.length > 0 ? (
                <section>
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2 mb-4">
                    <i className="ri-line-chart-line text-accent-600 w-5 h-5 flex items-center justify-center" />
                    数据分析
                    <span className="text-xs font-normal text-foreground-500">({matchedAnalyses.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {matchedAnalyses.map((item) => (
                      <Link
                        key={item.id}
                        to={`/analysis/${item.id}`}
                        className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
                      >
                        <div className="w-full h-36 bg-background-200">
                          <img
                            src={item.cover}
                            alt={item.title}
                            title={`${item.title} 数据分析专题`}
                            className="w-full h-full object-cover object-top group-hover:scale-[1.03] transition-transform duration-500"
                          />
                        </div>
                        <div className="p-4">
                          <h3 className="text-sm font-medium text-foreground-900 leading-snug group-hover:text-primary-700 transition-colors line-clamp-2">
                            {item.title}
                          </h3>
                          <p className="mt-1.5 text-xs text-foreground-600 line-clamp-2">{item.subtitle}</p>
                          <span className="mt-2 inline-block text-[11px] text-foreground-500">
                            {item.readTime} · 更新于 {item.updatedAt}
                          </span>
                        </div>
                      </Link>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>
          )}
        </>
      ) : (
        <div className="mt-8 bg-background-50 border border-background-200 rounded-lg py-16 px-6 text-center">
          <span className="w-16 h-16 rounded-full bg-primary-100 text-primary-600 flex items-center justify-center mx-auto">
            <i className="ri-search-line w-7 h-7 flex items-center justify-center text-3xl" />
          </span>
          <h2 className="mt-4 font-heading text-lg font-semibold text-foreground-900">
            输入关键词开始搜索
          </h2>
          <p className="mt-2 text-sm text-foreground-600 max-w-md mx-auto">
            支持赛事名称、参赛球队、赛事联赛与黑5数据关键词的模糊查询，也可以直接点击上方热门关键词。
          </p>
        </div>
      )}
    </div>
  );
}